export const pinchProject = {
  id: 4, 
  slug: 'pinch',
  title: 'Pinch', 
  description: 'Startup platform highlighting local restaurants and couriers.', 
  tags: ['mobile app', 'design', 'development'],
  image: '/pinch/Pinch - Cover.png',
  logo: '/pinch/Pinch - Logo.png',
  heroLogo: '/pinch/Pinch - Logo No BG.svg',
  heroImage: '/pinch/Pinch - Header.png',
  heroDescription: 'A startup platform connecting local restaurants with couriers.',
  headerBackground: '#FEC06A',
  meta: {
    role: ['Co-Founder', 'VP Design'],
    timeline: '3 months',
    tools: 'Figma, Flutter, Dart, Illustrator',
  },
  liveUrl: '',
  overview: {
    title: 'Overview',
    paragraphs: [
      'Pinch is a startup focused on providing a platform that highlights local restaurants and couriers. As Co-Founder and VP Design, I oversaw all design aspects, from mobile UI to website and marketing graphics, while also assisting with front-end development using Dart with Flutter.',
    ],
  },
  goal: {
    title: 'Goal',
    description: 'The platform focused on reducing operational friction for businesses, improving transparency across the order lifecycle, enabling efficient courier workflows, and meeting consumer expectations while differentiating Pinch through clarity and reliability.',
  },
  introduction: {
    title: 'Summary',
    description: 'Third-party food delivery platforms prioritize growth at the expense of businesses and couriers, leaving restaurants to bear high commissions and blame for issues outside their control. I led research, strategy, and design for Pinch, a multi-sided platform that improves transparency, control, and efficiency for businesses, couriers, and consumers while delivering a cohesive, approachable brand identity.',
  },
  reflections: {
    title: 'What\'s the problem?',
    paragraphs: [
      'Food delivery apps have been on the rise. However, businesses and couriers have taken a hit as the apps take a huge percentage of commission. This has caused businesses to price their food higher on food delivery apps. In addition, businesses are blamed for majority of issues that arise, when they have no control over the service past preparing the food.',
      {
        header: 'Research & Discovery',
        text: 'Competitive analysis of platforms such as Uber Eats, DoorDash, Grubhub, and SkipTheDishes showed similar commission-based models and limited tooling for businesses and couriers. Stakeholder conversations highlighted recurring issues around post-order visibility, delivery coordination, and unclear ownership of problems.',
        image: '/pinch/Pinch - Competitor Analysis.png',
      },
    ],
  },
  achievements: [
    {
      logo: '/YCombinator.png',
      title: 'Finalist out of 10,000 applicants chosen for an interview with YCombinator',
      subtitle: 'May 2021',
      url: 'https://www.ycombinator.com',
      linkText: 'YCombinator',
    },
    {
      logo: '/TheHub.png',
      title: 'Winner at The Hub\'s annual Startup competition for seed funding',
      subtitle: 'November 2020',
      url: 'https://www.utsc.utoronto.ca/research/hub-idea-startup',
      linkText: 'The Hub',
    },
    {
      logo: '/Cooperathon.png',
      title: 'Finalist at Cooperathon for \'Best Tech Solution\' & \'Best Overall Startup\'',
      subtitle: 'November 2020',
      url: 'https://www.youtube.com/watch?v=vMmSW_1SNU4',
      linkText: 'Cooperathon',
    },
  ],
  challenge: {
    title: 'Challenges & Design Focus',
    description: 'To inform the design direction, we analyzed existing products and competitor platforms operating in similar, multi-sided ecosystems. Reviewing how established apps serve restaurants, couriers, and customers revealed recurring patterns and pain points across mobile and web experiences. From this analysis, two core problem areas emerged that guided the design focus.',
    problemAreas: [
      'Scalability of Workflows',
      'Familiarity vs. Differentiation',
    ],
  },
  problems: [
    {
      id: 'problem-1',
      title: 'Scalability of Workflows',
      whatWasHappening: 'Early analysis showed that initial design decisions would need to scale across increasing numbers of users, orders, and locations without introducing brittle or one-off solutions.',
      keyFindings: [
        'Ad hoc flows break down quickly as order volume increases',
        'Duplicated logic across roles creates maintenance and usability issues',
        'Systems optimized for small scale do not translate to real operational complexity',
      ],
      designFocus: [
        'Modular, reusable components that support growth',
        'Shared system patterns across businesses, couriers, and consumers',
        'Clear state management to prevent workflow fragmentation',
      ],
    },
    {
      id: 'problem-2',
      title: 'Familiarity vs. Differentiation',
      whatWasHappening: 'Consumers arrive with strong expectations shaped by existing delivery platforms, while businesses and couriers need improved transparency and control not currently supported by incumbents.',
      keyFindings: [
        'Familiar interaction patterns reduce onboarding friction',
        'Differentiation is most effective when applied to ownership and visibility, not surface-level UI',
        'Over-innovation in core flows increases cognitive load under pressure',
      ],
      designFocus: [
        'Preserve familiar mental models for ordering and delivery',
        'Introduce differentiation through clearer status, ownership, and feedback loops',
        'Prioritize clarity over novelty in high-stakes moments',
      ],
    },
  ],
  designProcess: {
    title: 'Let\'s build this!',
    paragraphs: [
      'I started by defining a visual direction that felt clean, minimal, and modern, while still having enough personality to stand out. Given the data-heavy nature of the product, the design prioritizes clarity and restraint, allowing content and insights to take focus.',
      'Yellow was chosen as the primary accent to introduce energy and character. Often associated with alertness and appetite, it reinforces the idea of staying engaged with customer feedback, while neutral tones keep the interface calm and highly scannable.',
      'Typography and spacing were intentionally simple, emphasizing readability and hierarchy across dense interfaces. Together, these choices create a system that feels lightweight, approachable, and purposeful.',
    ],
    image: '/pinch/Pinch - Styles.svg',
  },
  results: {
    title: 'Results',
    description: 'Pinch was designed as a multi-sided system with centralized order visibility for businesses, streamlined task management for couriers, and clear status updates for consumers. Modular components ensure scalability without introducing friction across workflows.\n\nThe platform improved operational clarity across all user groups. Businesses gained visibility and control, couriers experienced less friction managing deliveries, and consumers benefited from more consistent service.',
    finalDesignRows: [
      {
        title: 'Customer',
        images: [
          '/pinch/Pinch - Customer Restaurant Page.png',
          '/pinch/Pinch - Customer Cart Page.png',
          '/pinch/Pinch - Customer Tracking.png',
        ],
      },
      {
        title: 'Courier',
        images: [
          '/pinch/Pinch - Courier Restaurant Page.png',
          '/pinch/Pinch - Courier Orders.png',
          '/pinch/Pinch - Courier Order History.png',
        ],
      },
      {
        title: 'Business',
        images: [
          '/pinch/Pinch - Business Menu - New Item.png',
          '/pinch/Pinch - Business Messages.png',
          '/pinch/Pinch - Business Couriers.png',
        ],
      },
    ],
    finalDesignFooterImage: '/pinch/Pinch - Final.svg',
  },
};
